
if (typeof window.__divanoLeadTimeRowDefined === 'undefined') {
window.__divanoLeadTimeRowDefined = true;

class DivanoLeadTimeRow extends HTMLElement {
  connectedCallback() {
    this._restricted = this.dataset.stockDisclosure === 'restricted';
    this._manifest = this._restricted ? null : this._readManifest();
    this._currentQty = 1;
    this._currentMeta = this._initialMeta();
    this._onVariantUpdate = this._onVariantUpdate.bind(this);
    this._onQtyUpdate = this._onQtyUpdate.bind(this);
    document.addEventListener('variant:update', this._onVariantUpdate);
    document.addEventListener('quantity-selector:update', this._onQtyUpdate);
    this._updateQtyDispatch();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this._onVariantUpdate);
    document.removeEventListener('quantity-selector:update', this._onQtyUpdate);
  }

  _initialMeta() {
    if (!this._manifest || !this._manifest.variants) return null;
    const vid = this.dataset.currentVariantId;
    if (vid && this._manifest.variants[String(vid)]) return this._manifest.variants[String(vid)];
    const keys = Object.keys(this._manifest.variants);
    return keys.length ? this._manifest.variants[keys[0]] : null;
  }

  _onQtyUpdate(event) {
    const q = event && event.detail && Number(event.detail.quantity);
    if (Number.isFinite(q) && q > 0) {
      this._currentQty = q;
      this._updateQtyDispatch();
    }
  }

  _readManifest() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const scriptEl = document.querySelector(
      `script[data-divano-lead-time-manifest][data-product-id="${cssEscape(productId)}"]`
    );
    if (!scriptEl) return null;
    try {
      return JSON.parse(scriptEl.textContent || '{}');
    } catch (err) {
      console.warn('[divano-lead-time-row] Failed to parse manifest:', err);
      return null;
    }
  }

  _onVariantUpdate(event) {
    if (this._restricted) {
      this._adoptServerRow(event);
      return;
    }
    if (!this._manifest) return;
    const variant = event.detail && event.detail.resource;
    if (!variant || !variant.id) return;

    const productId = event.detail.data && event.detail.data.productId;
    if (productId && productId !== this.dataset.productId) return;

    const meta = this._manifest.variants[String(variant.id)];
    if (!meta) return;

    let state = this._deriveState(meta);
    let copy = state ? this._deriveCopy(state, meta) : '';
    const inStockCopy = this._manifest.copy_templates && this._manifest.copy_templates.in_stock;
    if (!state && meta.stock === 'in_stock' && inStockCopy) {
      state = 'in_stock';
      copy = inStockCopy;
    }
    if (!state || !copy) {
      this.hidden = true;
      this._currentMeta = meta;
      return;
    }
    this.hidden = false;

    this.dataset.state = state;
    const indicator = this.querySelector('[data-role="indicator"]');
    if (indicator) indicator.dataset.state = state;
    const text = this.querySelector('[data-role="copy"]');
    if (text) text.textContent = copy;

    this._updateBatchesUI(meta);
    this._currentMeta = meta;
    this._updateQtyDispatch();
  }

  _adoptServerRow(event) {
    const variant = event.detail && event.detail.resource;
    if (!variant || !variant.id) return;
    const data = event.detail.data || {};
    if (data.productId && data.productId !== this.dataset.productId) return;

    const mySection = this.closest('[id^="shopify-section-"]');
    const source = event.target instanceof Element ? event.target.closest('[id^="shopify-section-"]') : null;
    if (mySection && source && source !== mySection) return;

    const html = data.html;
    let fresh = null;
    if (html && typeof html.querySelector === 'function' && this.dataset.blockId) {
      fresh = html.querySelector(
        `divano-lead-time-row[data-block-id="${cssEscape(this.dataset.blockId)}"]`
      );
    }
    const renderHasContent = !!(html && html.body && html.body.childElementCount > 0);
    if (!fresh && renderHasContent) return;
    const usable =
      fresh &&
      fresh.dataset.stockDisclosure === 'restricted' &&
      fresh.dataset.currentVariantId === String(variant.id) &&
      !fresh.hasAttribute('hidden');

    this.dataset.currentVariantId = String(variant.id);
    if (!usable) {
      this.hidden = true;
      this.dataset.state = '';
      delete this.dataset.dispatchFrom;
      this.innerHTML = '';
      return;
    }
    this.dataset.state = fresh.dataset.state || '';
    if (fresh.dataset.dispatchFrom) {
      this.dataset.dispatchFrom = fresh.dataset.dispatchFrom;
    } else {
      delete this.dataset.dispatchFrom;
    }
    this.innerHTML = fresh.innerHTML;
    this.hidden = false;
    this._updateQtyDispatch();
  }

  _deriveState(meta) {
    if (meta.lead_time_state) return meta.lead_time_state;
    const productState = this._manifest.product_defaults && this._manifest.product_defaults.lead_time_state;
    if (productState) return productState;
    if (meta.is_made_to_order) return 'preorder';
    if (meta.bridge_state) return meta.bridge_state;
    const threshold = Number(this._manifest && this._manifest.low_stock_threshold) || 0;
    if (threshold > 0 && meta.available === true && meta.inventory_tracked === 'shopify') {
      const onHand = Number(meta.inventory_quantity);
      if (Number.isFinite(onHand) && onHand > 0 && onHand <= threshold) return 'low_stock';
    }
    return null;
  }

  _deriveCopy(state, meta) {
    const templates = this._manifest.copy_templates || {};
    if (state === 'low_stock') {
      const tpl = templates.low_stock_only_n_left;
      const onHand = Number(meta && meta.inventory_quantity);
      if (!tpl || !Number.isFinite(onHand) || onHand <= 0) return '';
      return tpl.replace('__QTY__', String(onHand));
    }
    const defaults = this._manifest.product_defaults || {};
    const days = (meta && meta.lead_time_business_days) || defaults.lead_time_business_days;
    const month = (meta && meta.arrival_month) || (meta && meta.bridge_month) || defaults.arrival_month;
    const inHandQty = meta ? pickQty(meta.in_hand_qty, meta.bridge_in_hand) : undefined;
    const arrivals = (meta && meta.arrivals_schedule) || (meta && meta.bridge_arrivals) || [];
    const firstArrivalQty = (arrivals[0] && arrivals[0].qty) || (meta && meta.bridge_first_qty);

    switch (state) {
      case 'in_stock':
        if (inHandQty && Number(inHandQty) > 0 && arrivals.length > 0 && month && templates.in_stock_with_qty_more_coming) {
          return templates.in_stock_with_qty_more_coming
            .replace('__QTY__', String(inHandQty))
            .replace('__MONTH__', month);
        }
        if (inHandQty && Number(inHandQty) > 0 && templates.in_stock_with_qty) {
          return templates.in_stock_with_qty.replace('__QTY__', String(inHandQty));
        }
        if (days && Number(days) > 0 && templates.in_stock_with_days) {
          return templates.in_stock_with_days.replace('__DAYS__', String(days));
        }
        return '';
      case 'preorder':
        if (month && Number(firstArrivalQty) === 1 && templates.preorder_with_month_qty_one) {
          return templates.preorder_with_month_qty_one.replace('__MONTH__', month);
        }
        if (month && firstArrivalQty > 0 && templates.preorder_with_month_qty) {
          return templates.preorder_with_month_qty
            .replace('__MONTH__', month)
            .replace('__QTY__', String(firstArrivalQty));
        }
        if (month && templates.preorder_with_month) {
          return templates.preorder_with_month.replace('__MONTH__', month);
        }
        return templates.preorder_default || '';
      case 'backorder':
        if (month && templates.backorder_with_month) {
          return templates.backorder_with_month.replace('__MONTH__', month);
        }
        return templates.backorder_default || '';
      default:
        return '';
    }
  }

  _updateBatchesUI(meta) {
    const wrapper = this.querySelector('[data-role="batches"]');
    if (!wrapper) return;
    const list = this.querySelector('[data-role="batches-list"]');
    if (!list) return;
    const arrivals = (meta && meta.arrivals_schedule) || (meta && meta.bridge_arrivals) || [];
    if (arrivals.length === 0) {
      wrapper.hidden = true;
      list.innerHTML = '';
      return;
    }
    wrapper.hidden = false;
    const templates = this._manifest.copy_templates || {};
    const summary = this.querySelector('[data-role="batches-summary"]');
    const totalTemplate = templates.batches_heading_with_total;
    if (summary && totalTemplate) {
      const total = this._maxOrderable(meta);
      summary.textContent = totalTemplate.replace('__TOTAL__', String(total)).replace('{{ count }}', String(total));
    }
    const lineTemplate = templates.batch_line_with_qty || '{{ month_day }}: {{ qty }} units';
    list.innerHTML = arrivals
      .map((batch) => {
        const dt = formatBatchDate(batch.date);
        const line = lineTemplate
          .replace('__MONTH_DAY__', dt)
          .replace('{{ month_day }}', dt)
          .replace('__QTY__', String(batch.qty))
          .replace('{{ qty }}', String(batch.qty));
        const li = document.createElement('li');
        li.className = 'divano-lead-time-row__batch';
        li.textContent = line;
        return li.outerHTML;
      })
      .join('');
  }

  _updateQtyDispatch() {
    const el = this.querySelector('[data-role="qty-dispatch"]');
    if (!el) return;
    if (this._restricted) {
      const from = Number(this.dataset.dispatchFrom);
      const qty = this._currentQty || 1;
      el.hidden = !(Number.isFinite(from) && from >= 2 && qty >= from && !this.hidden);
      return;
    }
    const meta = this._currentMeta;
    const qty = this._currentQty || 1;
    const tmpl = ((this._manifest && this._manifest.copy_templates) || {}).qty_dispatch_consolidated;
    const hide = () => { el.hidden = true; el.textContent = ''; };
    if (!meta || !tmpl) return hide();
    const inHand = pickQty(meta.in_hand_qty, meta.bridge_in_hand);
    const arrivals = (meta.arrivals_schedule || meta.bridge_arrivals || [])
      .filter((b) => b && b.date)
      .slice()
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    if (qty < 2 || qty <= inHand || arrivals.length === 0) return hide();

    const maxOrderable = this._maxOrderable(meta);
    if (qty > maxOrderable) {
      const maxTmpl = ((this._manifest && this._manifest.copy_templates) || {}).qty_dispatch_max;
      if (!maxTmpl) return hide();
      el.textContent = maxTmpl.replace('__QTY__', String(maxOrderable)).replace('{{ qty }}', String(maxOrderable));
      el.hidden = false;
      return;
    }

    let cum = inHand, shipDate = null;
    for (const b of arrivals) { cum += Number(b.qty) || 0; if (cum >= qty) { shipDate = b.date; break; } }
    if (!shipDate) shipDate = arrivals[arrivals.length - 1].date;
    const dateStr = formatBatchDate(shipDate);
    el.textContent = tmpl
      .replace('__DATE__', dateStr).replace('{{ date }}', dateStr)
      .replace('__QTY__', String(qty)).replace('{{ qty }}', String(qty));
    el.hidden = false;
  }

  _maxOrderable(meta) {
    if (!meta) return 0;
    const inHand = pickQty(meta.in_hand_qty, meta.bridge_in_hand);
    const batchSum = (meta.arrivals_schedule || meta.bridge_arrivals || []).reduce((s, b) => s + (Number(b && b.qty) || 0), 0);
    const supplyTotal = inHand + batchSum;
    const invQty = Number(meta.inventory_quantity);
    const denies = meta.inventory_policy == null ? true : meta.inventory_policy !== 'continue';
    return (denies && Number.isFinite(invQty) && invQty > 0) ? invQty : supplyTotal;
  }
}

function pickQty(typed, bridge) {
  if (typed !== null && typed !== undefined && typed !== '') {
    const t = Number(typed);
    if (Number.isFinite(t)) return t;
  }
  const b = Number(bridge);
  return Number.isFinite(b) ? b : 0;
}

function formatBatchDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-AU', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return iso;
  }
}

function cssEscape(s) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(s);
  }
  return String(s).replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

if (!customElements.get('divano-lead-time-row')) {
  customElements.define('divano-lead-time-row', DivanoLeadTimeRow);
}

}
